import { useState } from "react";
import { ChevronDown, ChevronRight, RefreshCw, X, Calculator } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatIDR, fmtDate } from "@/lib/constants";
import ProfitStatusBadge from "./ProfitStatusBadge";

/**
 * R227 (2026-10-20): dialog drill-down pembentuk Profit/Selisih & status
 * kelengkapan biaya — komponen yang SAMA dipakai Ringkasan Proyek,
 * Monitoring Proyek, dan Cost Control. READ-ONLY (tidak mengubah transaksi
 * sumber). Mendukung Escape (bawaan Dialog), loading/empty/error + coba lagi.
 */

const CATEGORIES = [
  { key: "bank_unallocated", label: "Mutasi Bank Belum Dialokasikan" },
  { key: "unclassified", label: "Pengeluaran Belum Diklasifikasikan ke Item/RAB" },
  { key: "petty_cash_pending", label: "Petty Cash Menunggu Verifikasi 1" },
  { key: "conflicts", label: "Transaksi Konflik (Nominal/Status)" },
];

/**
 * R229 (2026-10-20, user request "PERBAIKI DIALOG DAN TAMPILAN STATUS
 * UNKNOWN"): view penjelasan untuk proyek TANPA entri Cost Control — status
 * kanonis unknown. Dipakai caller ketika `summaries` tidak memuat proyek tsb
 * (tabel CC kosong = data minimum tidak tersedia). Murni presentasi — tidak
 * mengubah angka/rumus engine.
 */
export function buildUnknownProfitView(projectId) {
  const empty = { count: 0, total: 0, items: [] };
  return {
    project_id: projectId,
    as_of: new Date().toISOString().slice(0, 10),
    scope_note: null,
    revenue_basis: { label: "Total Harga Kontrak (Cost Control)", value: 0, valid: false, source: "cost_control_items", item_count: 0, updated_at: null },
    cost_recognized: 0,
    pending: { total: 0, bank_unallocated: empty, unclassified: empty, petty_cash_pending: empty, conflicts: empty },
    selisih: null,
    margin: null,
    completeness: {
      status: "unknown",
      label: "Belum Dapat Dipastikan",
      reasons: [{ code: "tabel_cc_kosong", label: "Tabel Cost Control/RAB kosong — data minimum tidak tersedia. Isi tabel Cost Control terlebih dahulu, lalu minta pengguna berwenang melakukan Konfirmasi Kelengkapan.", count: 0, amount: 0 }],
      updated_at: new Date().toISOString().slice(0, 10),
      confirmation: null,
    },
    labels: { selisih: "Selisih Sementara", margin: "Margin Sementara" },
  };
}

function CategoryBlock({ catKey, label, data }) {
  const [open, setOpen] = useState(false);
  const count = data?.count || 0;
  return (
    <div className="border border-slate-200 bg-white" data-testid={`dd-cat-${catKey}`}>
      <button type="button" onClick={() => setOpen(!open)} disabled={count === 0}
        data-testid={`dd-cat-toggle-${catKey}`}
        className={`w-full px-3 py-2 flex items-center justify-between text-left ${count > 0 ? "hover:bg-slate-50" : "opacity-60 cursor-default"}`}>
        <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5">
          {count > 0 ? (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : <span className="w-3" />}
          {label}
        </span>
        <span className="text-[11px] tabular-nums text-slate-600">
          <b>{count}</b> transaksi · <b className={count > 0 ? "text-amber-700" : ""}>{formatIDR(data?.total || 0)}</b>
        </span>
      </button>
      {open && count > 0 && (
        <div className="border-t border-slate-100 max-h-48 overflow-y-auto" data-testid={`dd-cat-items-${catKey}`}>
          {(data.items || []).map((it, i) => (
            <div key={`${it.id || i}`} className="px-3 py-1.5 flex items-center justify-between gap-2 text-[11px] border-b border-slate-50">
              <div className="min-w-0">
                <div className="text-slate-800 truncate">{it.description || "—"}</div>
                <div className="text-[10px] text-slate-500">{[it.date ? fmtDate(it.date) : null, it.source, it.conflict].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="text-right shrink-0 tabular-nums">
                <div className="font-semibold text-slate-800">{formatIDR(it.amount)}</div>
                {it.allocated_total != null && it.remaining > 0 && (
                  <div className="text-[10px] text-amber-700">sisa {formatIDR(it.remaining)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProfitDrilldownDialog({ open, onOpenChange, view, loading, error, onRetry, projectName }) {
  const comp = view?.completeness || {};
  const basis = view?.revenue_basis || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="profit-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0B1D3A]">
            <Calculator size={16} /> Rincian Profit & Kelengkapan Biaya{projectName ? ` — ${projectName}` : ""}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-500 text-sm" data-testid="dd-loading">Memuat rincian…</div>
        ) : error ? (
          <div className="py-10 text-center space-y-3" data-testid="dd-error">
            <div className="text-rose-700 text-sm">Gagal memuat rincian.</div>
            <div className="flex items-center justify-center gap-2">
              <Button type="button" variant="outline" onClick={onRetry} data-testid="dd-retry" className="rounded-none text-xs">
                <RefreshCw size={12} className="mr-1" /> Coba Lagi
              </Button>
              {/* R229: Tutup SELALU tersedia di setiap state. */}
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="dd-close" className="rounded-none text-xs">
                <X size={12} className="mr-1" /> Tutup
              </Button>
            </div>
          </div>
        ) : !view ? (
          <div className="py-10 text-center space-y-3" data-testid="dd-empty">
            <div className="text-slate-500 text-sm">Belum ada data perhitungan untuk proyek ini.</div>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="dd-close" className="rounded-none text-xs">
              <X size={12} className="mr-1" /> Tutup
            </Button>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 flex-wrap" data-testid="dd-status-row">
              <ProfitStatusBadge view={view} testid="dd-status-badge" />
              <span className="text-[10px] text-slate-500">Tanggal acuan: <b>{view.as_of ? fmtDate(view.as_of) : "—"}</b> · Diperbarui: <b>{comp.updated_at ? fmtDate(comp.updated_at) : "—"}</b></span>
            </div>

            {view.scope_note && (
              <div className="text-[10px] text-slate-600 bg-slate-50 border border-slate-200 px-2 py-1" data-testid="dd-scope-note">{view.scope_note}</div>
            )}

            <div className="grid grid-cols-2 gap-2" data-testid="dd-figures">
              <div className="border border-slate-200 p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Dasar Pendapatan</div>
                <div className="font-bold tabular-nums text-[#0B1D3A]" data-testid="dd-basis">{basis.valid ? formatIDR(basis.value) : "—"}</div>
                <div className="text-[9px] text-slate-500">{basis.label} · sumber: {basis.source} · {basis.item_count} item</div>
              </div>
              <div className="border border-slate-200 p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Biaya Diakui</div>
                <div className="font-bold tabular-nums text-rose-700" data-testid="dd-cost-recognized">{formatIDR(view.cost_recognized)}</div>
                <div className="text-[9px] text-slate-500">terverifikasi · terklasifikasi · teralokasi</div>
              </div>
              <div className="border border-slate-200 p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Biaya Tertunda</div>
                <div className="font-bold tabular-nums text-amber-700" data-testid="dd-pending-total">{formatIDR(view.pending?.total || 0)}</div>
                <div className="text-[9px] text-slate-500">belum masuk Biaya Diakui</div>
              </div>
              <div className="border border-slate-200 p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{view.labels?.selisih} · {view.labels?.margin}</div>
                <div className={`font-bold tabular-nums ${comp.status === "complete" ? (view.selisih >= 0 ? "text-emerald-700" : "text-rose-700") : "text-slate-700"}`} data-testid="dd-selisih">
                  {/* R229: selisih null (unknown tanpa data) → "—", jangan Rp0. */}
                  {view.selisih === null || view.selisih === undefined ? "—" : formatIDR(view.selisih)}
                </div>
                <div className="text-[10px] tabular-nums text-slate-600" data-testid="dd-margin">
                  {view.margin === null || view.margin === undefined ? "Margin belum dapat dihitung" : `${view.margin.toFixed(2)}%`}
                </div>
                <div className="text-[9px] text-slate-400">Margin = Selisih ÷ Dasar Pendapatan × 100</div>
              </div>
            </div>

            <div className="space-y-1.5" data-testid="dd-categories">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pembentuk Biaya Tertunda & Konflik</div>
              {CATEGORIES.map((c) => <CategoryBlock key={c.key} catKey={c.key} label={c.label} data={view.pending?.[c.key]} />)}
            </div>

            {(comp.reasons || []).length > 0 && (
              <div className="border border-amber-200 bg-amber-50 px-3 py-2" data-testid="dd-reasons">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 mb-1">Alasan Status</div>
                <ul className="text-[11px] text-amber-900 space-y-0.5 list-disc pl-4">
                  {comp.reasons.map((r) => (
                    <li key={r.code}>{r.label}{r.count ? ` — ${r.count} transaksi · ${formatIDR(r.amount)}` : ""}</li>
                  ))}
                </ul>
              </div>
            )}

            {comp.confirmation && (
              <div className="text-[10px] text-slate-500" data-testid="dd-confirmation">
                Konfirmasi terakhir: <b>{comp.confirmation.status}</b> oleh {comp.confirmation.confirmed_by_name || "—"} · {comp.confirmation.confirmed_at ? fmtDate(comp.confirmation.confirmed_at) : "—"}
                {comp.confirmation.fingerprint_match === false && <span className="text-rose-700 font-semibold"> · data berubah setelah konfirmasi — perlu ditinjau ulang</span>}
              </div>
            )}

            <div className="flex justify-end pt-1">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="dd-close" className="rounded-none text-xs">
                <X size={12} className="mr-1" /> Tutup
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
